import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useNavigate } from "react-router-dom";
import { useUnsavedChanges } from "../context/UnsavedChangesContext";
import EventConclusionCard from "./EventConclusionCard";
import ExpectedCodeBandsHint, {
  expectedCodeBandLabel,
  type CodeBandVisibility,
} from "./ExpectedCodeBandsHint";
import LeaveEventCreatorDialog from "./LeaveEventCreatorDialog";

function modeLabel(eventCreation: string): string {
  if (eventCreation === "rising") return "Rising only";
  if (eventCreation === "resolving") return "Resolving only";
  if (eventCreation === "both") return "Rising + resolving";
  return "Event";
}

type SummaryFieldProps = {
  label: string;
  value: React.ReactNode;
};

function SummaryField({ label, value }: SummaryFieldProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600} component="div">
        {value}
      </Typography>
    </Box>
  );
}

export type CreatorConclusionSummaryProps = {
  eventCreation: string;
  baseLabel: string;
  subLabel: string;
  baseDigit?: number;
  subDigit?: number;
  showRisingDetails: boolean;
  showResolvingDetails: boolean;
  risingSeverity?: string | number | null;
  risingDescriptions?: Parameters<typeof EventConclusionCard>[0]["descriptions"];
  risingPrimaryLanguage?: string | null;
  risingContractName?: string | null;
  resolvingSeverity?: string | number | null;
  resolvingDescriptions?: Parameters<typeof EventConclusionCard>[0]["descriptions"];
  resolvingPrimaryLanguage?: string | null;
  resolvingContractName?: string | null;
  loading: boolean;
  canFinish: boolean;
  onFinish: () => void;
};

export default function CreatorConclusionSummary({
  eventCreation,
  baseLabel,
  subLabel,
  baseDigit,
  subDigit,
  showRisingDetails,
  showResolvingDetails,
  risingSeverity,
  risingDescriptions,
  risingPrimaryLanguage,
  risingContractName,
  resolvingSeverity,
  resolvingDescriptions,
  resolvingPrimaryLanguage,
  resolvingContractName,
  loading,
  canFinish,
  onFinish,
}: CreatorConclusionSummaryProps) {
  const navigate = useNavigate();
  const { setHasUnsavedChanges, discardDraft } = useUnsavedChanges();
  const [leaveDialogOpen, setLeaveDialogOpen] = React.useState(false);
  const [leaveBusy, setLeaveBusy] = React.useState(false);

  const closeLeaveDialog = React.useCallback(() => {
    if (leaveBusy) return;
    setLeaveDialogOpen(false);
  }, [leaveBusy]);

  const handleLeaveKeepDraft = React.useCallback(() => {
    setHasUnsavedChanges(false);
    setLeaveDialogOpen(false);
    navigate("/");
  }, [navigate, setHasUnsavedChanges]);

  const handleDiscardAndLeave = React.useCallback(async () => {
    try {
      setLeaveBusy(true);
      await discardDraft();
      setHasUnsavedChanges(false);
      setLeaveDialogOpen(false);
      navigate("/");
    } finally {
      setLeaveBusy(false);
    }
  }, [discardDraft, navigate, setHasUnsavedChanges]);

  const showBands = baseDigit != null && subDigit != null;
  const bandVisibility: CodeBandVisibility =
    eventCreation === "rising"
      ? "rising"
      : eventCreation === "resolving"
        ? "resolving"
        : "both";

  return (
    <Box sx={{ maxWidth: 880, mx: "auto", px: { xs: 1, sm: 2 }, py: 1 }}>
      <Stack spacing={3}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Review & create
          </Typography>
          <Chip
            label={modeLabel(eventCreation)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 1.25 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: "auto" }}>
            Confirm your function selection and event details, then create the event
            {eventCreation === "both" ? "s" : ""} in the catalog.
          </Typography>
        </Box>

        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <AccountTreeOutlinedIcon color="action" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Selected functions
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, sm: showBands ? 6 : 12 }}>
              <Stack spacing={2}>
                <SummaryField label="Base function" value={baseLabel} />
                <SummaryField label="Sub function" value={subLabel} />
              </Stack>
            </Grid>
            {showBands && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <SummaryField
                  label={expectedCodeBandLabel(bandVisibility)}
                  value={
                    <ExpectedCodeBandsHint
                      baseDigit={baseDigit}
                      subDigit={subDigit}
                      show={bandVisibility}
                      sx={{ mt: 0.5 }}
                    />
                  }
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {(showRisingDetails || showResolvingDetails) && (
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 0.6, fontSize: "0.7rem" }}
            >
              Event details
            </Typography>
            <Grid container spacing={2}>
              {showRisingDetails && (
                <Grid size={{ xs: 12, md: showResolvingDetails ? 6 : 12 }}>
                  <EventConclusionCard
                    kind="rising"
                    severity={risingSeverity ?? null}
                    descriptions={risingDescriptions}
                    primaryLanguage={risingPrimaryLanguage}
                    contractName={risingContractName}
                  />
                </Grid>
              )}
              {showResolvingDetails && (
                <Grid size={{ xs: 12, md: showRisingDetails ? 6 : 12 }}>
                  <EventConclusionCard
                    kind="resolving"
                    severity={resolvingSeverity ?? null}
                    descriptions={resolvingDescriptions}
                    primaryLanguage={resolvingPrimaryLanguage}
                    contractName={resolvingContractName}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        <Box sx={{ textAlign: "center" }}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={<CloseIcon />}
              onClick={() => setLeaveDialogOpen(true)}
              disabled={loading || leaveBusy}
              sx={{ minWidth: 180, px: 3 }}
            >
              Discard event
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onFinish}
              disabled={loading || !canFinish || leaveBusy}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DoneIcon />}
              sx={{ minWidth: 220, px: 4 }}
            >
              {loading ? "Creating…" : "Create event"}
              {eventCreation === "both" ? "s" : ""}
            </Button>
          </Stack>
          {!canFinish && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1.5 }}>
              Complete all required event data steps before creating.
            </Typography>
          )}
        </Box>

        <LeaveEventCreatorDialog
          open={leaveDialogOpen}
          onClose={closeLeaveDialog}
          onStay={closeLeaveDialog}
          onLeaveKeepDraft={handleLeaveKeepDraft}
          onDiscardAndLeave={() => void handleDiscardAndLeave()}
          leaveBusy={leaveBusy}
        />
      </Stack>
    </Box>
  );
}
